import React, {
  MutableRefObject,
  useCallback,
  useState,
  useEffect,
  VFC,
} from 'react';

import { throttle } from 'lodash';

import CollapsedNav from './CollapsedNav';
import ExpandedNav from './ExpandedNav';
import styles from './InPageNav.scss';
import { NullableHTMLDivElement, Section } from '../../types';
import useTranslate from '../../utils/translations';
import useSeriesData from '../../utils/useSeriesData';
import useTranslationsData from '../../utils/useTranslationsData';

const HEADER_OFFSET = 50;

type InPageNavProps = {
  blockRefs: MutableRefObject<NullableHTMLDivElement[]>;
  sections: Section[];
  title: string;
  databookUrl?: string;
  groupId?: string;
  articleId?: string;
};

const InPageNav: VFC<InPageNavProps> = ({
  blockRefs,
  sections,
  title,
  databookUrl,
  groupId,
  articleId,
}) => {
  const t = useTranslate();
  const navTitles = {
    translation: t('Language version'),
    toc: t('Table of contents'),
    databook: t('View databook'),
    series: t('Report timeline'),
  };
  const [expandedIcon, setExpandedIcon] = useState('');
  const seriesData = useSeriesData();
  const translations = useTranslationsData(groupId, articleId);

  const getPosition = useCallback(
    (scrollY: number): number => {
      const findSectionTop = (section: Section): number => {
        const element = (blockRefs?.current || [])[section.index];
        return section.index && element ? element?.offsetTop : 0;
      };

      const findSectionBottom = (scrollY: number, section: Section): number => {
        const nextSection = (blockRefs?.current || [])[section.end];
        return nextSection?.offsetTop || scrollY + 1;
      };

      for (const section of sections) {
        if (
          scrollY >= findSectionTop(section) &&
          scrollY < findSectionBottom(scrollY, section)
        ) {
          return section.index;
        }
        for (const subsection of section.subsections || []) {
          if (
            scrollY >= findSectionTop(subsection) &&
            scrollY < findSectionBottom(scrollY, subsection)
          ) {
            return subsection.index;
          }
        }
      }
      return 0;
    },
    [blockRefs, sections],
  );

  const [currentPosition, setCurrentPosition] = useState(() =>
    getPosition(window.scrollY),
  );

  useEffect(() => {
    const onScroll = (): void => {
      const scrollY = Math.round(
        document.querySelector('.headroom--pinned')
          ? window.scrollY + HEADER_OFFSET
          : window.scrollY,
      );
      setCurrentPosition(getPosition(scrollY));
    };

    const throttledScroll = throttle(onScroll, 200);
    window.addEventListener('scroll', throttledScroll);
    return (): void => {
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [getPosition]);

  const showToc = !!sections.length;
  const showSeriesData = !!seriesData.length;
  const showTranslations = translations?.length > 1;
  // Return early if nothing to show
  if (!(showToc || databookUrl || showSeriesData || showTranslations)) {
    return <></>;
  }

  const scrollTo = (index: number): void => {
    const section = index && blockRefs && blockRefs.current[index];
    const updatedY = section ? section.offsetTop : 0;

    if (section || !index) {
      // When scrolling up, leave space for mintel-nav
      window.scrollTo({
        top: index > currentPosition ? updatedY : updatedY - HEADER_OFFSET,
        behavior: 'smooth',
      });
    }
  };

  const isInSection = (section: Section): boolean => {
    const { subsections } = section;
    if (subsections && subsections.length > 0) {
      const lastSubsection = subsections[subsections.length - 1];
      return (
        currentPosition >= section.index &&
        currentPosition <= lastSubsection.index
      );
    }
    return false;
  };

  const setExpandedIconBtn = (iconTitle: string): void =>
    setExpandedIcon(iconTitle);

  const handleMouseLeave = (): void => {
    window.setTimeout(() => {
      setExpandedIcon('');
    }, 400);
  };

  const handleEscapeClose = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      handleMouseLeave();
    }
  };
  return (
    <nav className={styles['in-page-nav']}>
      <div
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleEscapeClose}
        className={styles.inner}
        data-testid="in-page-nav"
      >
        <CollapsedNav
          databookUrl={databookUrl}
          expandedIcon={expandedIcon}
          navTitles={navTitles}
          showSeriesData={showSeriesData}
          setExpandedIcon={setExpandedIconBtn}
          showTranslations={showTranslations}
          showToc={showToc}
        />
        <ExpandedNav
          currentPosition={currentPosition}
          expandedIcon={expandedIcon}
          navTitles={navTitles}
          show={!!expandedIcon}
          sections={sections}
          scrollTo={scrollTo}
          title={title}
          isInSection={isInSection}
          seriesData={seriesData}
          translations={translations}
        />
      </div>
    </nav>
  );
};

InPageNav.displayName = 'components/InPageNav';
export default InPageNav;
